import { Link } from 'react-router-dom'
import { Vote, Calendar, Trophy, Megaphone } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type ElectionPhase = 'pre-campaign' | 'campaign' | 'election-day' | 'results'

export interface ElectionCountdownProps {
  currentTick: number
  nextElectionTick: number | null
  termLengthTicks: number
  campaignStartTick?: number | null
  electionStatus?: 'scheduled' | 'campaigning' | 'voting' | 'completed' | null
  latestCompletedElectionId?: string | null
  nationId?: string
  className?: string
}

function getPhase(
  currentTick: number,
  nextElectionTick: number | null,
  campaignStartTick: number | null | undefined,
  electionStatus: string | null | undefined
): ElectionPhase {
  // If an election just completed and we have no upcoming one, show results
  if (electionStatus === 'completed' || electionStatus === 'voting') {
    return 'results'
  }

  if (nextElectionTick === null) return 'pre-campaign'

  if (currentTick >= nextElectionTick) return 'election-day'

  if (
    campaignStartTick != null &&
    currentTick >= campaignStartTick &&
    currentTick < nextElectionTick
  ) {
    return 'campaign'
  }

  return 'pre-campaign'
}

export function ElectionCountdown({
  currentTick,
  nextElectionTick,
  termLengthTicks,
  campaignStartTick,
  electionStatus,
  latestCompletedElectionId,
  nationId,
  className,
}: ElectionCountdownProps) {
  const phase = getPhase(currentTick, nextElectionTick, campaignStartTick, electionStatus)
  const ticksRemaining =
    nextElectionTick !== null ? Math.max(0, nextElectionTick - currentTick) : null

  // Term progress calculation
  const termElapsed =
    nextElectionTick !== null && termLengthTicks > 0
      ? termLengthTicks - (nextElectionTick - currentTick)
      : 0
  const termProgress =
    termLengthTicks > 0
      ? Math.min(100, Math.max(0, (termElapsed / termLengthTicks) * 100))
      : 0
  // Where the campaign starts on the progress bar
  const campaignMarkerPercent =
    termLengthTicks > 0
      ? ((termLengthTicks - (campaignStartTick != null && nextElectionTick !== null
          ? nextElectionTick - campaignStartTick
          : 4)) /
          termLengthTicks) *
        100
      : 75

  return (
    <Card
      className={cn(
        'transition-all duration-300',
        phase === 'campaign' && 'border-purple-400 dark:border-purple-600 shadow-md shadow-purple-100 dark:shadow-purple-900/20',
        phase === 'election-day' && 'border-purple-500 dark:border-purple-500 shadow-lg shadow-purple-200 dark:shadow-purple-900/30',
        phase === 'results' && 'border-green-400 dark:border-green-600',
        className
      )}
    >
      <CardContent className="pt-5 pb-4 px-5">
        {/* Header row */}
        <div className="flex items-center justify-between mb-3">
          <PhaseLabel phase={phase} />
          <PhaseIcon phase={phase} />
        </div>

        {/* Main countdown display */}
        <CountdownDisplay
          phase={phase}
          ticksRemaining={ticksRemaining}
          latestCompletedElectionId={latestCompletedElectionId}
          nationId={nationId}
        />

        {/* Term progress bar */}
        {nextElectionTick !== null && termLengthTicks > 0 && phase !== 'results' && (
          <TermProgressBar
            progress={termProgress}
            campaignMarkerPercent={campaignMarkerPercent}
            phase={phase}
          />
        )}
      </CardContent>
    </Card>
  )
}

// ============================================================================
// Phase Label
// ============================================================================

function PhaseLabel({ phase }: { phase: ElectionPhase }) {
  switch (phase) {
    case 'pre-campaign':
      return (
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Electoral Term
        </span>
      )
    case 'campaign':
      return (
        <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">
          Campaign Season
        </span>
      )
    case 'election-day':
      return (
        <span className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wide animate-pulse">
          Election Day
        </span>
      )
    case 'results':
      return (
        <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide">
          Results Available
        </span>
      )
  }
}

// ============================================================================
// Phase Icon
// ============================================================================

function PhaseIcon({ phase }: { phase: ElectionPhase }) {
  const base = 'h-5 w-5'
  switch (phase) {
    case 'pre-campaign':
      return <Calendar className={cn(base, 'text-muted-foreground')} />
    case 'campaign':
      return <Megaphone className={cn(base, 'text-purple-500 dark:text-purple-400')} />
    case 'election-day':
      return <Vote className={cn(base, 'text-purple-600 dark:text-purple-300 animate-pulse')} />
    case 'results':
      return <Trophy className={cn(base, 'text-green-500 dark:text-green-400')} />
  }
}

// ============================================================================
// Countdown Display
// ============================================================================

function CountdownDisplay({
  phase,
  ticksRemaining,
  latestCompletedElectionId,
  nationId,
}: {
  phase: ElectionPhase
  ticksRemaining: number | null
  latestCompletedElectionId?: string | null
  nationId?: string
}) {
  if (phase === 'election-day') {
    return (
      <div className="text-center py-1">
        <p className="text-lg font-bold text-purple-700 dark:text-purple-300">
          Votes being counted...
        </p>
      </div>
    )
  }

  if (phase === 'results') {
    return (
      <div className="text-center py-1">
        {latestCompletedElectionId && nationId ? (
          <Link
            to={`/nations/${nationId}/elections/${latestCompletedElectionId}`}
            className="text-lg font-bold text-green-600 dark:text-green-400 hover:underline"
          >
            View Election Results →
          </Link>
        ) : (
          <p className="text-lg font-bold text-green-600 dark:text-green-400">
            Election Complete
          </p>
        )}
      </div>
    )
  }

  if (ticksRemaining === null) {
    return (
      <div className="text-center py-1">
        <p className="text-lg font-bold text-muted-foreground">No election scheduled</p>
      </div>
    )
  }

  return (
    <div className="text-center py-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help inline-block">
            <span
              className={cn(
                'text-2xl font-bold tabular-nums',
                phase === 'campaign'
                  ? 'text-purple-600 dark:text-purple-400'
                  : 'text-foreground'
              )}
            >
              {ticksRemaining}
            </span>
            <span
              className={cn(
                'text-sm ml-1.5',
                phase === 'campaign'
                  ? 'text-purple-500 dark:text-purple-400'
                  : 'text-muted-foreground'
              )}
            >
              {ticksRemaining === 1 ? 'day' : 'days'} until election
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>1 tick = 1 in-game day</p>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

// ============================================================================
// Term Progress Bar
// ============================================================================

function TermProgressBar({
  progress,
  campaignMarkerPercent,
  phase,
}: {
  progress: number
  campaignMarkerPercent: number
  phase: ElectionPhase
}) {
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
        <span>Term start</span>
        <span>Election</span>
      </div>
      <div className="relative h-2 w-full rounded-full bg-muted overflow-hidden">
        {/* Filled progress */}
        <div
          className={cn(
            'absolute left-0 top-0 h-full rounded-full transition-all duration-500',
            phase === 'campaign'
              ? 'bg-purple-500 dark:bg-purple-400'
              : phase === 'election-day'
                ? 'bg-purple-600 dark:bg-purple-400'
                : 'bg-primary'
          )}
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
        {/* Campaign start marker */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="absolute top-0 h-full w-0.5 bg-purple-400 dark:bg-purple-500 cursor-help"
              style={{ left: `${campaignMarkerPercent}%` }}
            />
          </TooltipTrigger>
          <TooltipContent>
            <p>Campaign season begins</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}
